import { useStressTestStore } from '../../store/stressTestStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { fmt } from '../../utils/numberFormat';
import { puToVolts } from '../../utils/electricalConstants';
import { VOLTAGE_OPERATIONAL_MIN_PU, VOLTAGE_OPERATIONAL_MAX_PU } from '../../utils/voltageConstants';
import { LOADING_COMPLIANCE_LIMIT_PCT } from '../../utils/loadingConstants';
import { measureLabel, violationLabel } from '../../utils/planningLabels';

/**
 * "Abhilfe finden": after a run with limit breaches, one click proposes and
 * evaluates measure sets (fuses, cable + fuse, tap, trafo, combination) on
 * the same base case; "Übernehmen" puts a candidate into the scenario and
 * re-runs. Every candidate line is a real run, so it answers "does this
 * carry?" for the whole meshed unit, not only for the limiting element.
 */
export function RemedyCard() {
  const run = useStressTestStore(s => s.run);
  const remedies = useStressTestStore(s => s.remedies);
  const isFinding = useStressTestStore(s => s.isFindingRemedies);
  const remedyError = useStressTestStore(s => s.remedyError);
  const findRemedies = useStressTestStore(s => s.findRemedies);
  const applyRemedy = useStressTestStore(s => s.applyRemedy);
  const isCalculating = useStressTestStore(s => s.isCalculating);
  const stressElements = useStressTestStore(s => s.stressElements);
  const setSelectedElement = useNetworkUIStore(s => s.setSelectedElement);

  if (!run) return null;
  const s = run.summary;
  const hasViolations = s.maxLoadingWithPct > LOADING_COMPLIANCE_LIMIT_PCT || (s.minVoltageWithPu ?? 1) < VOLTAGE_OPERATIONAL_MIN_PU || (s.maxVoltageWithPu ?? 1) > VOLTAGE_OPERATIONAL_MAX_PU;
  if (!hasViolations && !remedies) return null;

  const nameOf = (id: string) => stressElements.find(e => e.id === id)?.name ?? run.impacts.find(i => i.elementId === id)?.name ?? id;
  const select = (id: string) => {
    const el = stressElements.find(e => e.id === id);
    if (el) setSelectedElement(el);
  };

  return (
    <div className="rounded-lg border border-orange-300 dark:border-orange-600 bg-white dark:bg-primary-700 overflow-hidden" data-testid="remedy-card">
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-800 dark:text-primary-100">Abhilfe</h3>
          {remedies && <span className="text-[10px] text-gray-500 dark:text-primary-400">{remedies.runsEvaluated} Läufe</span>}
        </div>

        {!remedies && (
          <>
            <p className="text-[11px] text-gray-600 dark:text-primary-300 mb-2 leading-snug">
              Das Szenario reißt Grenzwerte. Die Suche schlägt Sicherung, Kabel mit Sicherung, Stufenschalter und Trafo vor und rechnet jeden Vorschlag durch.
            </p>
            <button
              type="button"
              onClick={() => void findRemedies()}
              disabled={isFinding || isCalculating}
              className="w-full px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
              data-testid="find-remedies"
            >
              {isFinding ? 'Vorschläge werden gerechnet …' : 'Abhilfe finden'}
            </button>
          </>
        )}
        {remedyError && <p className="text-[11px] text-red-600 dark:text-red-400 mt-1">{remedyError}</p>}

        {remedies && (
          <div className="space-y-2">
            <p className="text-[11px] text-gray-600 dark:text-primary-300 leading-snug">
              Verletzt: {remedies.violations.slice(0, 4).map((v, idx) => (
                <button key={`${v.kind}-${v.elementId}-${idx}`} type="button" onClick={() => select(v.elementId)} className="text-accent-primary hover:underline mr-1">{violationLabel(v)}</button>
              ))}
              {remedies.violations.length > 4 && `+${remedies.violations.length - 4}`}
            </p>
            {remedies.candidates.length === 0 && (
              <p className="text-[11px] text-gray-600 dark:text-primary-300">{remedies.note ?? 'Keine Vorschläge.'}</p>
            )}
            <ul className="space-y-1.5" data-testid="remedy-candidates">
              {remedies.candidates.map(c => (
                <li key={c.candidate.id} className={`rounded border p-2 ${c.resolves ? 'border-green-300 dark:border-green-700 bg-green-50/60 dark:bg-green-900/10' : 'border-gray-200 dark:border-primary-600'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-gray-900 dark:text-primary-100">{c.candidate.label}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.resolves ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : !c.converged ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>
                      {c.resolves ? 'löst' : !c.converged ? 'nicht konvergiert' : `${c.remainingViolations} offen`}
                    </span>
                  </div>
                  <ul className="mt-1 text-[11px] text-gray-700 dark:text-primary-200">
                    {c.candidate.measures.map(m => <li key={m.kind + m.elementId}>· {measureLabel(m, nameOf)}</li>)}
                  </ul>
                  {c.converged && (
                    <p className="text-[10px] text-gray-500 dark:text-primary-400 mt-0.5 tabular-nums">
                      max. {fmt(c.maxLoadingPct, 0)} % · {fmt(puToVolts(c.minVoltagePu ?? 1), 0)}–{fmt(puToVolts(c.maxVoltagePu ?? 1), 0)} V
                    </p>
                  )}
                  <p className="text-[10px] text-gray-500 dark:text-primary-400 mt-0.5 leading-snug">{c.candidate.rationale}</p>
                  <button
                    type="button"
                    onClick={() => void applyRemedy(c.candidate)}
                    disabled={isCalculating}
                    className="mt-1 text-[11px] font-medium text-accent-primary hover:underline disabled:opacity-50"
                  >
                    Übernehmen und neu rechnen
                  </button>
                </li>
              ))}
            </ul>
            {remedies.note && remedies.candidates.length > 0 && (
              <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-snug">{remedies.note}</p>
            )}
            <button type="button" onClick={() => void findRemedies()} disabled={isFinding || isCalculating} className="text-[11px] text-accent-primary hover:underline disabled:opacity-50">
              {isFinding ? 'Vorschläge werden gerechnet …' : 'Erneut suchen'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
